/** How the portal writes money, counts, dates and names, shared by every screen. */

const CURRENCY_SYMBOL: Record<string, string> = {
  USD: "$",
  CAD: "$",
  AUD: "$",
  EUR: "€",
  GBP: "£",
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Whole amounts without cents, others with two; a minus sign for a negative amount. */
export function money(cents: number, currency: string): string {
  const symbol = CURRENCY_SYMBOL[currency] ?? "";
  const dollars = Math.abs(cents) / 100;
  const body = Number.isInteger(dollars)
    ? dollars.toLocaleString("en-US")
    : dollars.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${cents < 0 ? "-" : ""}${symbol}${body}`;
}

/** Always with cents: "$330.00". */
export function money2(cents: number, currency: string): string {
  const symbol = CURRENCY_SYMBOL[currency] ?? "";
  return `${symbol}${(cents / 100).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function count(n: number): string {
  return n.toLocaleString("en-US");
}

/** The date part ("YYYY-MM-DD") of a sqlite date or datetime as a local Date, or null. */
function toDate(s: string | null): Date | null {
  if (!s) return null;
  const d = new Date(`${s.slice(0, 10)}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "2026-08-21" to "Aug 21"; an unreadable date comes back as it was. */
export function shortDate(s: string | null): string {
  const d = toDate(s);
  if (!d) return s ?? "";
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

/** "2026-08-21" to "Aug 21, 2026". */
export function longDate(s: string | null): string {
  const d = toDate(s);
  return d ? `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}` : "";
}

/** "Jul 1 to Jul 31, 2026" from a period pair, or null when either end is missing. */
export function periodLabel(start: string | null, end: string | null): string | null {
  const a = toDate(start);
  const b = toDate(end);
  if (!a || !b) return null;
  return `${MONTHS[a.getMonth()]} ${a.getDate()} to ${MONTHS[b.getMonth()]} ${b.getDate()}, ${b.getFullYear()}`;
}

/** Up to two initials from a name or a label like "J. Marshall". */
export function initialsOf(name: string): string {
  const parts = name.split(/[\s.]+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0][0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? "") : "";
  return (first + last).toUpperCase();
}

/** "1.2 MB", "240 KB", or "" when the size is not known. */
export function humanSize(bytes: number | null): string {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  const mb = bytes / (1024 * 1024);
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
}
